import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAlertQueueActions } from './AlertQueueContext';
import { useAppResources } from './AppResourcesContext';
import {
  compareResourceOrder,
  getStreakCounterBefore,
  RESOURCE_BEAT_SPACING_MS,
  RESOURCE_FLIGHT_DURATION_MS,
  RESOURCE_RECEIVE_DURATION_MS,
  RESOURCE_ORDER,
} from '../utils/resourceCelebrationTiming';

const ResourceCelebrationContext = createContext(null);

const normalizePositiveInteger = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }
  return Math.trunc(numericValue);
};

export const useResourceCelebration = () => {
  const context = useContext(ResourceCelebrationContext);
  if (!context) {
    return {
      flights: [],
      targetImpactVersions: {},
      registerTarget: () => () => {},
      getTargetRect: () => null,
      queueResourceCelebration: () => null,
      queueStellineCelebration: () => null,
      queueStreakCelebration: () => null,
      completeCelebration: () => {},
      startCelebration: () => {},
      triggerTargetImpact: () => {},
      deliverCelebration: () => {},
      getPresentedCounterValue: (_type, value) => value,
    };
  }
  return context;
};

export const ResourceCelebrationProvider = ({ children }) => {
  const { acceptRewardAlert, registerRewardPresenter } = useAlertQueueActions();
  const { appResources } = useAppResources();
  const resourcesRef = useRef(appResources);
  resourcesRef.current = appResources;
  const accountActionsRef = useRef(acceptRewardAlert);
  const latestAccountActionsRef = useRef(acceptRewardAlert);
  latestAccountActionsRef.current = acceptRewardAlert;
  const targetsRef = useRef(new Map());
  const idRef = useRef(0);
  const startedIdsRef = useRef(new Set());
  const nextAvailableStartRef = useRef(0);
  const [flights, setFlights] = useState([]);
  // Actions need the latest queue even when several receipts arrive in one batch.
  const flightsRef = useRef([]);
  const deliveredIdsRef = useRef(new Set());
  const [deliveredIds, setDeliveredIds] = useState(new Set());
  const [targetImpactVersions, setTargetImpactVersions] = useState({});

  useLayoutEffect(() => {
    if (accountActionsRef.current !== acceptRewardAlert) {
      accountActionsRef.current = acceptRewardAlert;
      startedIdsRef.current.clear();
      nextAvailableStartRef.current = 0;
      flightsRef.current = [];
      deliveredIdsRef.current = new Set();
      setDeliveredIds(deliveredIdsRef.current);
      setFlights([]);
      setTargetImpactVersions({});
    }
  }, [acceptRewardAlert]);

  const registerTarget = useCallback((type, element) => {
    if (!type) {
      return () => {};
    }

    if (element) {
      targetsRef.current.set(type, element);
    } else {
      targetsRef.current.delete(type);
    }

    return () => {
      if (targetsRef.current.get(type) === element) {
        targetsRef.current.delete(type);
      }
    };
  }, []);

  const getTargetRect = useCallback((type) => {
    const element = targetsRef.current.get(type);
    if (!element || typeof element.getBoundingClientRect !== 'function') {
      return null;
    }
    return element.getBoundingClientRect();
  }, []);

  const completeCelebration = useCallback((id) => {
    startedIdsRef.current.delete(id);
    flightsRef.current = flightsRef.current.filter((flight) => flight.id !== id);
    setFlights(flightsRef.current);
    if (deliveredIdsRef.current.has(id)) {
      deliveredIdsRef.current = new Set(deliveredIdsRef.current);
      deliveredIdsRef.current.delete(id);
      setDeliveredIds(deliveredIdsRef.current);
    }
  }, []);

  const startCelebration = useCallback((flight) => {
    if (!flightsRef.current.includes(flight)) return;
    startedIdsRef.current.add(flight.id);
    nextAvailableStartRef.current = Math.max(nextAvailableStartRef.current, flight.startAt + Math.max(
      RESOURCE_BEAT_SPACING_MS,
      flight.holdMs + RESOURCE_FLIGHT_DURATION_MS + RESOURCE_RECEIVE_DURATION_MS,
    ));
  }, []);

  const triggerTargetImpact = useCallback((type) => {
    if (!type) return;
    setTargetImpactVersions((currentVersions) => ({
      ...currentVersions,
      [type]: (currentVersions[type] || 0) + 1,
    }));
  }, []);

  const deliverCelebration = useCallback((id, { pulseTarget = true } = {}) => {
    if (accountActionsRef.current !== latestAccountActionsRef.current) return;
    const flight = flightsRef.current.find((entry) => entry.id === id);
    if (!flight || deliveredIdsRef.current.has(id)) return;
    deliveredIdsRef.current = new Set([...deliveredIdsRef.current, id]);
    setDeliveredIds(deliveredIdsRef.current);
    if (pulseTarget) triggerTargetImpact(flight.type);
    flight.onImpact?.();
  }, [triggerTargetImpact]);

  const getPresentedCounterValue = useCallback((type, authoritativeValue) => {
    if (accountActionsRef.current !== acceptRewardAlert) return authoritativeValue;
    // Arrived flights stay mounted during the receive pulse but no longer hold a counter.
    const pending = flights.filter((flight) => flight.type === type && !deliveredIds.has(flight.id));
    if (type === 'streak') return pending[0]?.counterBefore ?? authoritativeValue;
    if (type !== 'stelline' && type !== 'learned_terms') return authoritativeValue;
    return Math.max(0, authoritativeValue - pending.reduce((total, flight) => total + flight.delta, 0));
  }, [acceptRewardAlert, flights, deliveredIds]);

  const queueResourceCelebration = useCallback((type, options = {}) => {
    if (!type || acceptRewardAlert !== latestAccountActionsRef.current) {
      return null;
    }

    let delta = normalizePositiveInteger(options.delta);
    if (delta == null) {
      return null;
    }

    if (options.rewardAlert) {
      const acceptedAlert = acceptRewardAlert(options.rewardAlert);
      if (!acceptedAlert) return null;
      if (type !== 'streak') {
        delta = normalizePositiveInteger(type === 'gettoncini' ? acceptedAlert.amount : acceptedAlert.delta);
      }
      if (delta == null) return null;
    }

    const id = `${type}-${Date.now()}-${idRef.current += 1}`;
    const delayMs = Math.max(0, Number(options.delayMs) || 0);
    const holdMs = Math.max(0, Number(options.holdMs) || 850);
    const queuedAt = Date.now();
    const requestedStartAt = queuedAt + delayMs;

    const scheduleFlights = (currentFlights) => {
      const startedFlights = currentFlights.filter((flight) => startedIdsRef.current.has(flight.id));
      const pendingFlights = currentFlights.filter((flight) => !startedIdsRef.current.has(flight.id));
      // Preserve the first reward's entry delay even when later rewards arrive
      // without a delay. Only reorder flights that have not appeared yet.
      let nextStartAt = Math.max(queuedAt, nextAvailableStartRef.current,
        pendingFlights.length ? Math.min(...pendingFlights.map((flight) => flight.startAt)) : requestedStartAt);
      const existingIndex = pendingFlights.findIndex((flight) => flight.type === type);
      const existing = pendingFlights[existingIndex];
      const onImpact = typeof options.onImpact === 'function' ? options.onImpact : null;
      const incomingFlight = {
        id,
        type,
        delta,
        queuedAt,
        requestedStartAt,
        holdMs,
        onImpact,
        counterBefore: type === 'streak'
          ? Math.max(0, Number(options.counterBefore ?? resourcesRef.current?.streak?.current_streak) || 0)
          : null,
      };
      if (existing) {
        // Resource identity is independent of the reward source or coin expiry.
        // Every accepted contribution and balance update belongs to one flight.
        pendingFlights[existingIndex] = {
          ...existing,
          id,
          delta: existing.delta + delta,
          requestedStartAt: Math.max(existing.requestedStartAt, requestedStartAt),
          holdMs: Math.max(existing.holdMs, holdMs),
          onImpact: () => {
            existing.onImpact?.();
            onImpact?.();
          },
        };
      } else {
        pendingFlights.push(incomingFlight);
      }
      const scheduledFlights = pendingFlights.sort(compareResourceOrder).map((flight) => {
        const startAt = Math.max(nextStartAt, flight.requestedStartAt);
        nextStartAt = startAt + Math.max(RESOURCE_BEAT_SPACING_MS,
          flight.holdMs + RESOURCE_FLIGHT_DURATION_MS + RESOURCE_RECEIVE_DURATION_MS);
        if (flight.startAt === startAt) return flight;
        return { ...flight, startAt, delayMs: startAt - flight.queuedAt };
      });
      return [...startedFlights, ...scheduledFlights];
    };
    flightsRef.current = scheduleFlights(flightsRef.current);
    setFlights(flightsRef.current);

    return id;
  }, [acceptRewardAlert]);

  // Register before child passive effects can queue hydrated or signup rewards.
  useLayoutEffect(() => {
    const unregisterPresenters = RESOURCE_ORDER.map((type) => (
      registerRewardPresenter(type, (alert) => {
        // Guest potential rewards belong to the account offer, not a reward popup.
        if (alert.isAnonymous !== true || type === 'streak') {
          queueResourceCelebration(type, {
            delta: type === 'streak' ? 1 : type === 'gettoncini'
              ? alert.amount
              : (alert.actualDelta === 0 && alert.potentialDelta > 0 ? 0 : alert.delta),
            delayMs: alert.delay,
            ...(type === 'streak' ? { counterBefore: getStreakCounterBefore(alert.streak) } : {}),
          });
        }
        // Consume resource notifications even when there is no actual award.
        return true;
      })
    ));
    return () => unregisterPresenters.forEach((unregister) => unregister());
  }, [registerRewardPresenter, queueResourceCelebration]);

  const queueStellineCelebration = useCallback((options = {}) => (
    queueResourceCelebration('stelline', options)
  ), [queueResourceCelebration]);

  const queueStreakCelebration = useCallback((options = {}) => (
    queueResourceCelebration('streak', options)
  ), [queueResourceCelebration]);

  const value = useMemo(() => ({
    flights,
    targetImpactVersions,
    registerTarget,
    getTargetRect,
    queueResourceCelebration,
    queueStellineCelebration,
    queueStreakCelebration,
    completeCelebration,
    startCelebration,
    triggerTargetImpact,
    deliverCelebration,
    getPresentedCounterValue,
  }), [
    flights,
    targetImpactVersions,
    registerTarget,
    getTargetRect,
    queueResourceCelebration,
    queueStellineCelebration,
    queueStreakCelebration,
    completeCelebration,
    startCelebration,
    triggerTargetImpact,
    deliverCelebration,
    getPresentedCounterValue,
  ]);

  return (
    <ResourceCelebrationContext.Provider value={value}>
      {children}
    </ResourceCelebrationContext.Provider>
  );
};
